/**
 * Deliverables collection — typed CRUD on top of the generic store.
 *
 * Deliverables are first-class objects per account (Ask #1): what each
 * customer is owed, its status, feeding health + the weekly update.
 * Wraps `db` so views deal in `Deliverable` records and so we can
 * centralise id minting (uuid) and timestamp stamping. Modelled on
 * `tickets.ts` — when we cut over to Firestore the only thing that
 * changes is the adapter selection in `store.ts`.
 */

import { db, nowIso, uuid } from './store';
import type { Deliverable } from './types';

export const DELIVERABLE_STATUSES = [
  'owed',
  'in-progress',
  'delivered',
  'accepted',
] as const;

export async function listDeliverables(): Promise<Deliverable[]> {
  return db.list('deliverables');
}

export async function getDeliverable(id: string): Promise<Deliverable | null> {
  return db.get('deliverables', id);
}

export function subscribeDeliverables(
  fn: (records: Deliverable[]) => void,
): () => void {
  return db.subscribe('deliverables', fn);
}

/** What a caller supplies to create a deliverable — we mint the rest. */
export interface NewDeliverableInput {
  accountId: string;
  accountName: string;
  name: string;
  owner?: string;
  dueDate?: string | null;
  status?: Deliverable['status'];
  note?: string;
}

/** Insert a new deliverable (id + timestamps minted here). */
export async function saveDeliverable(
  input: NewDeliverableInput,
): Promise<Deliverable> {
  const ts = nowIso();
  const record: Deliverable = {
    id: uuid(),
    accountId: input.accountId,
    accountName: input.accountName,
    name: input.name,
    owner: input.owner,
    dueDate: input.dueDate ?? null,
    status: input.status ?? 'owed',
    note: input.note,
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('deliverables', record);
  return record;
}

/** Patch a deliverable by id. Re-stamps `updatedAt`. */
export async function updateDeliverable(
  id: string,
  patch: Partial<Omit<Deliverable, 'id' | 'createdAt' | 'updatedAt'>>,
): Promise<Deliverable> {
  return db.update('deliverables', id, { ...patch, updatedAt: nowIso() });
}

export async function removeDeliverable(id: string): Promise<void> {
  await db.remove('deliverables', id);
}
